import { buildSubmissionBody } from "@/lib/recaptcha";

/**
 * Posts a form to one of the handler's `/submit/*` routes, and throws unless it
 * confirms the submission.
 *
 * These go out as ordinary CORS requests on purpose. They used to be sent with
 * `mode: "no-cors"`, which resolves with an opaque response whatever comes back
 * — so every rejection looked exactly like a success, and the modal told people
 * "We received your request successfully" while the submission was dropped on
 * the floor. That is how the staging deploy came to lose every audit request:
 * its origin is not on the reCAPTCHA key's domain list, so the handler rejected
 * each token with a 400 that the browser never let us see.
 *
 * The url-encoded body keeps this a simple request, so there is still no
 * preflight, but the origin now has to be on the handler's CORS allowlist
 * (`CORS_SETTINGS` in qs-form-handler) for the reply to be readable at all.
 */
export async function submitToFormHandler(
  formAction: string,
  formData: FormData,
  recaptchaAction: string,
): Promise<void> {
  const body = await buildSubmissionBody(formData, recaptchaAction);
  const response = await fetch(formAction, { method: "POST", body });
  if (response.ok) return;

  // The handler answers with `{ status: false, message }` and tells us which of
  // reCAPTCHA, validation or an upstream write refused it. Nothing renders the
  // message — the forms show one fixed failure notice — so log it, or a future
  // outage is once again invisible from the browser.
  const detail = await response.text().catch(() => "");
  const error = new Error(`Form handler rejected the submission (${response.status}). ${detail}`);
  console.error(error);
  throw error;
}
